import { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ease } from '../../core/format/motion';
import { PageErrorBoundary } from '../../ui/PageErrorBoundary';
import { lazyWithRetry } from '../../ui/lazyWithRetry';
import { legalDocs } from '../../data/legal';
import { NotFound } from './NotFound';
import { Home } from '../home/Home';

// Alleen de homepage zit in de eerste download: daar komt het meeste verkeer binnen.
// Elke andere pagina is een eigen stuk code, via lazyWithRetry (herkansen bij een haperend
// netwerk, één keer herladen na een nieuwe deploy).
const Shop = lazyWithRetry(() => import('../catalog/Shop').then(m => m.Shop), 'shop');
const Product = lazyWithRetry(() => import('../product/Product').then(m => m.Product), 'product');
const Checkout = lazyWithRetry(() => import('../checkout/Checkout').then(m => m.Checkout), 'checkout');
const OrderReturn = lazyWithRetry(() => import('../checkout/OrderReturn').then(m => m.OrderReturn), 'order-return');
const Routines = lazyWithRetry(() => import('../routines/Routines').then(m => m.Routines), 'routines');
const Treatments = lazyWithRetry(() => import('../salon/Treatments').then(m => m.Treatments), 'treatments');
const Journal = lazyWithRetry(() => import('../journal/Journal').then(m => m.Journal), 'journal');
const Article = lazyWithRetry(() => import('../journal/Article').then(m => m.Article), 'article');
const About = lazyWithRetry(() => import('../about/About').then(m => m.About), 'about');
const Contact = lazyWithRetry(() => import('../contact/Contact').then(m => m.Contact), 'contact');
const Legal = lazyWithRetry(() => import('../legal/Legal').then(m => m.Legal), 'legal');
const Account = lazyWithRetry(() => import('../account/Account').then(m => m.Account), 'account');
const NewsletterConfirm = lazyWithRetry(() => import('../newsletter/NewsletterConfirm').then(m => m.NewsletterConfirm), 'newsletter-confirm');

/**
 * Rustig vlak op de plek van de pagina; houdt de footer onder de vouw tijdens het laden.
 * De stipjes komen pas na een halve seconde: bij een snel netwerk zie je ze nooit, bij een
 * traag netwerk zie je dat er iets gebeurt in plaats van een leeg scherm.
 */
function PageLoading({
  label
}: {
  label: string;
}) {
  return <div role="status" className="flex min-h-[60vh] items-center justify-center" data-id="element-7998" data-mp="f696uz70mbj0">
      <span className="sr-only" data-id="element-7999" data-mp="1csyi7znly0hj">{label}</span>
      <span className="flex gap-1.5 opacity-0 motion-safe:animate-[fadeIn_200ms_ease-out_500ms_forwards] motion-reduce:opacity-100" aria-hidden="true" data-id="element-8000" data-mp="1cqm09mhunq4h">
        {[0, 1, 2].map(i => <span key={i} className="h-1.5 w-1.5 rounded-full bg-muted/50 motion-safe:animate-pulse" style={{
        animationDelay: `${i * 150}ms`
      }} data-id="element-8001" data-mp="12so329i0iizq" />)}
      </span>
    </div>;
}

/**
 * Pagina's met een korte fade-in.
 *
 * Bewust géén uitgangsanimatie (AnimatePresence mode="wait"): die wachtte tot
 * de oude pagina weg was vóór de nieuwe verscheen, en bleef bij snel of
 * gelijktijdig navigeren (bijv. uitloggen + doorklikken) soms hangen — dan
 * bleef het scherm leeg. Een fade-in geeft dezelfde rust zonder dat risico.
 */
export function AnimatedRoutes() {
  const location = useLocation();
  const reduce = useReducedMotion();
  // Mijn omgeving regelt zelf de overgang tussen onderdelen; het zijmenu blijft staan.
  const pageKey = location.pathname.startsWith('/account') ? '/account' : location.pathname;
  return <motion.div key={pageKey} initial={reduce ? false : {
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2,
    ease
  }} data-id="element-8002" data-mp="2lt6v4n1tp5p1">
      <PageErrorBoundary key={pageKey} data-id="element-8003" data-mp="2qtl3y7b90jo4">
        <Suspense fallback={<PageLoading label="Pagina laden…" data-id="element-8005" data-mp="2vr4il8buyygl" />} data-id="element-8004" data-mp="2qqgsqpzd8nny">
          <Routes location={location} data-id="element-8006" data-mp="2vquq9e3ccf7q">
            <Route path="/" element={<Home data-id="element-8008" data-mp="wesdemp82j31" />} data-id="element-8007" data-mp="we8scx49zeks" />
            <Route path="/webshop" element={<Shop data-id="element-8010" data-mp="wgd76a3ppxzu" />} data-id="element-8009" data-mp="wfk53mfp7wo1" />
            <Route path="/webshop/:id" element={<Product data-id="element-8012" data-mp="11eelyiu3vb5i" />} data-id="element-8011" data-mp="11d3k4ok80dh1" />
            <Route path="/afrekenen" element={<Checkout data-id="element-8014" data-mp="2rugyyk8sol84" />} data-id="element-8013" data-mp="11dlns697ptyz" />
            <Route path="/bestelling/:orderId" element={<OrderReturn data-id="element-8016" data-mp="2rwjx5rk3pknx" />} data-id="element-8015" data-mp="2rtnuxr1809to" />
            <Route path="/routines" element={<Routines data-id="element-8018" data-mp="2mub2mqrkyajp" />} data-id="element-8017" data-mp="2mvme7cr9omld" />
            <Route path="/behandelingen" element={<Treatments data-id="element-8020" data-mp="30uvhxcyado8g" />} data-id="element-8019" data-mp="2mv4cw1pqzi6l" />
            <Route path="/journal" element={<Journal data-id="element-8022" data-mp="30ucawra6ck97" />} data-id="element-8021" data-mp="30u40d61ixr16" />
            <Route path="/journal/:slug" element={<Article data-id="element-8024" data-mp="2wulea6xyv9wr" />} data-id="element-8023" data-mp="2wtk1ny1ayfcx" />
            <Route path="/over-ons" element={<About data-id="element-8026" data-mp="293z7sk3m1xov" />} data-id="element-8025" data-mp="2wubmqex9wuoy" />
            <Route path="/contact" element={<Contact data-id="element-8028" data-mp="294hanzks3a5r" />} data-id="element-8027" data-mp="293682332k8g8" />
            <Route path="/nieuwsbrief/bevestig" element={<NewsletterConfirm data-id="element-8030" data-mp="245nxk89ell64" />} data-id="element-8029" data-mp="291ld4ccuenhu" />
            <Route path="/account/*" element={<Suspense fallback={<PageLoading label="Mijn omgeving laden…" data-id="element-8033" data-mp="2hxkk7wsaz2v4" />} data-id="element-8032" data-mp="2hx2t1ekhzeu2">
                  <Account data-id="element-8034" data-mp="2d5awmnenuew0" />
                </Suspense>} data-id="element-8031" data-mp="246pl3c7lw04q" />
            {legalDocs.map(d => <Route key={d.path} path={d.path} element={<Legal data-id="element-8036" data-mp="1led8lwn8nigv" />} data-id="element-8035" data-mp="1penmdvh9vd6a" />)}
            <Route path="*" element={<NotFound data-id="element-8038" data-mp="1ldvcd00kt48y" />} data-id="element-8037" data-mp="1ldbxjs682vz5" />
          </Routes>
        </Suspense>
      </PageErrorBoundary>
    </motion.div>;
}